import React, { useEffect, useState } from 'react';
import { adminApi, fmtDate } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusPill } from '@/components/fintech/StatusPill';
import { toast } from 'sonner';
import { Ban, CheckCircle2, Eye } from 'lucide-react';

export default function AdminUsersPage() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockingId, setBlockingId] = useState(null);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (kycFilter !== 'all') params.kyc = kycFilter;
    adminApi.get('/admin/users', { params }).then(r => setItems(r.data.items || []));
  };
  useEffect(() => { load(); }, [kycFilter]);

  const doBlock = async () => {
    if (!blockingId) return;
    await adminApi.post(`/admin/users/${blockingId}/block`, { reason: blockReason });
    setBlockingId(null); setBlockReason(''); load();
    toast.success('Blocked');
  };
  const doUnblock = async (id) => { await adminApi.post(`/admin/users/${id}/unblock`); load(); toast.success('Unblocked'); };

  return (
    <div className="space-y-4">
      <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">Users</h1>
      <Card className="bg-white"><CardContent className="p-4 flex flex-wrap gap-3">
        <Input placeholder="Search email or name" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} className="w-64" data-testid="admin-users-search" />
        <Select value={kycFilter} onValueChange={setKycFilter}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="all">All KYC statuses</SelectItem>
          <SelectItem value="NOT_STARTED">Not started</SelectItem>
          <SelectItem value="IN_REVIEW">In review</SelectItem>
          <SelectItem value="APPROVED">Approved</SelectItem>
          <SelectItem value="REJECTED">Rejected</SelectItem>
        </SelectContent></Select>
        <Button onClick={load}>Search</Button>
      </CardContent></Card>

      <Card className="bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--secondary))]"><tr>
            <th className="p-3 text-left">Email</th><th className="p-3 text-left">Name</th><th className="p-3 text-left">Type</th><th className="p-3 text-left">KYC</th><th className="p-3 text-left">Created</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map(u => (
              <tr key={u.user_id} className="border-t">
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.first_name} {u.last_name}</td>
                <td className="p-3 capitalize">{u.account_type}</td>
                <td className="p-3"><StatusPill status={u.kyc_status} /></td>
                <td className="p-3 text-xs">{fmtDate(u.created_at)}</td>
                <td className="p-3">{u.blocked ? <StatusPill status="REJECTED" /> : <StatusPill status="ACTIVE" />}</td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(u)}><Eye className="h-4 w-4" /></Button>
                    {u.blocked
                      ? <Button size="sm" variant="outline" onClick={() => doUnblock(u.user_id)}><CheckCircle2 className="h-4 w-4 mr-1" />Unblock</Button>
                      : <Button size="sm" variant="destructive" onClick={() => setBlockingId(u.user_id)}><Ban className="h-4 w-4 mr-1" />Block</Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No users</div>}
      </Card>

      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selected?.email}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              <div><span className="text-[hsl(var(--muted-foreground))]">User ID:</span> <code className="text-xs">{selected.user_id}</code></div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Name:</span> {selected.first_name} {selected.last_name}</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Phone:</span> {selected.phone || '—'}</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Citizenship:</span> {selected.citizenship || '—'}</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Account:</span> {selected.account_type}</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">KYC:</span> {selected.kyc_status}</div>
              <div className="mt-3">
                <div className="font-semibold">Balances</div>
                {Object.entries(selected.balances || {}).map(([c, v]) => (
                  <div key={c} className="flex justify-between"><span>{c}</span><span className="tabular-nums">{Number(v).toFixed(2)}</span></div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!blockingId} onOpenChange={o => !o && setBlockingId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Block user</DialogTitle></DialogHeader>
          <Input placeholder="Reason" value={blockReason} onChange={e => setBlockReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockingId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doBlock}>Block</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
